import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import type { Evaluation } from '@/types/evaluation.types';
import { EvaluationStatus } from '@/types/evaluation.types';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from './StatusBadge';
import { ActivateDialog } from './ActivateDialog';
import { ConcludeDialog } from './ConcludeDialog';
import { CloseDialog } from './CloseDialog';
import { ReactivateDialog } from './ReactivateDialog';
import { EditableEvaluationName } from '@/components/dashboard/EditableEvaluationName';
import { EditableEvaluationSummary } from '@/components/dashboard/EditableEvaluationSummary';
import { canActivateEvaluation, getActivationBlockers } from '@/services/evaluation-status';
import { AlertCircle, CheckCircle2, XCircle, Lock, Play, RotateCcw, CheckCheck, Info } from 'lucide-react';

interface StatusCardProps {
  evaluation: Evaluation;
  onActivate: (evaluation: Evaluation) => void;
  onConclude: (evaluation: Evaluation) => void;
  onClose: (evaluation: Evaluation, reason?: string) => void;
  onReactivate: (evaluation: Evaluation) => void;
  onUpdateName: (name: string) => void;
  onUpdateSummary: (summary: string | undefined) => void;
}

export function StatusCard({ evaluation, onActivate, onConclude, onClose, onReactivate, onUpdateName, onUpdateSummary }: StatusCardProps) {
  const { t } = useTranslation(['dashboard', 'common']);

  const formatDate = (date: Date): string => {
    return formatDistanceToNow(date, { addSuffix: true });
  };

  // Get activation blockers if in Organizing state
  const blockers =
    evaluation.status === EvaluationStatus.Organizing
      ? getActivationBlockers(evaluation)
      : [];

  // Get appropriate icon based on status
  const getStatusIcon = () => {
    switch (evaluation.status) {
      case EvaluationStatus.Organizing:
        return <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" aria-hidden="true" />;
      case EvaluationStatus.Active:
        return <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" aria-hidden="true" />;
      case EvaluationStatus.Concluded:
        return <Lock className="h-5 w-5 text-purple-600 dark:text-purple-400" aria-hidden="true" />;
      case EvaluationStatus.Closed:
        return <XCircle className="h-5 w-5 text-gray-600 dark:text-gray-400" aria-hidden="true" />;
      default:
        return null;
    }
  };

  return (
    <Card>
      {/* Evaluation Name and Badge */}
      <CardHeader className="pb-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0 group">
            <EditableEvaluationName name={evaluation.name} onSave={onUpdateName} />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge variant="secondary">{t('dashboard:hostView')}</Badge>
            <StatusBadge status={evaluation.status} size="sm" />
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          {t('dashboard:subtitle')}
        </p>
      </CardHeader>

      {/* Status and Summary Section - Side by Side */}
      <CardContent className="border-t pt-4 pb-4">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Right on desktop, First on mobile: Evaluation Summary */}
          <div className="space-y-4 md:order-2 md:pl-6 md:border-l pb-6 md:pb-0">
            <EditableEvaluationSummary summary={evaluation.summary} onSave={onUpdateSummary} />
          </div>

          {/* Left on desktop, Second on mobile: Status Information and Actions */}
          <div className="space-y-3 md:order-1 border-t md:border-t-0 pt-6 md:pt-0">
            <div className="flex items-center gap-2">
              {getStatusIcon()}
              <h3 className="text-sm font-medium">{t('dashboard:status.card.title')}</h3>
            </div>

            {/* Status changed date */}
            <p className="text-xs text-muted-foreground">
              {t('dashboard:status.card.changedOn', {
                date: formatDate(evaluation.statusChangedDate),
              })}
            </p>

            {/* Activation info (if in Organizing state) */}
            {evaluation.status === EvaluationStatus.Organizing && (
              <div className="rounded-md bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 p-2 flex items-start gap-1.5">
                <Info className="h-3 w-3 mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-400" aria-hidden="true" />
                <p className="text-xs text-blue-900 dark:text-blue-200">
                  {t('dashboard:status.activate.info')}
                </p>
              </div>
            )}

            {/* Closed reason (if applicable) */}
            {evaluation.status === EvaluationStatus.Closed && evaluation.closedReason && (
              <div className="rounded-md bg-muted p-2">
                <p className="text-xs">
                  <span className="font-medium">{t('dashboard:status.card.closedReason')}</span>
                  <span className="text-muted-foreground ml-1">{evaluation.closedReason}</span>
                </p>
              </div>
            )}

            {/* Activation blockers (if in Organizing state) */}
            {evaluation.status === EvaluationStatus.Organizing && blockers.length > 0 && (
              <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 p-2">
                <p className="text-xs font-medium text-amber-900 dark:text-amber-200 mb-1">
                  {t('dashboard:status.activate.requirements')}
                </p>
                <ul className="space-y-0.5">
                  {blockers.map((blocker, index) => (
                    <li key={index} className="text-xs text-amber-800 dark:text-amber-300 flex items-start gap-1.5">
                      <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>{t(blocker)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Scoring locked message (if Concluded or Closed) */}
            {(evaluation.status === EvaluationStatus.Concluded ||
              evaluation.status === EvaluationStatus.Closed) && (
              <div className="rounded-md bg-muted p-2 flex items-start gap-1.5">
                <Lock className="h-3 w-3 mt-0.5 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
                <p className="text-xs text-muted-foreground">
                  {evaluation.status === EvaluationStatus.Concluded
                    ? t('dashboard:status.scoringLocked.concluded')
                    : t('dashboard:status.scoringLocked.closed')}
                </p>
              </div>
            )}

            {/* Status Action Buttons */}
            <div className="pt-3 flex flex-wrap gap-2">
              {evaluation.status === EvaluationStatus.Organizing && (
                <ActivateDialog evaluation={evaluation} onActivate={onActivate}>
                  <Button size="sm" disabled={!canActivateEvaluation(evaluation).isValid}>
                    <Play className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t('dashboard:status.activate.button')}
                  </Button>
                </ActivateDialog>
              )}

              {evaluation.status === EvaluationStatus.Active && (
                <>
                  <ConcludeDialog evaluation={evaluation} onConclude={onConclude}>
                    <Button size="sm" variant="default">
                      <CheckCheck className="mr-2 h-4 w-4" aria-hidden="true" />
                      {t('dashboard:status.conclude.button')}
                    </Button>
                  </ConcludeDialog>
                  <CloseDialog evaluation={evaluation} onClose={onClose}>
                    <Button size="sm" variant="outline">
                      <XCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                      {t('dashboard:status.close.button')}
                    </Button>
                  </CloseDialog>
                </>
              )}

              {(evaluation.status === EvaluationStatus.Concluded || evaluation.status === EvaluationStatus.Closed) && (
                <>
                  <ReactivateDialog evaluation={evaluation} onReactivate={onReactivate}>
                    <Button size="sm" variant="default">
                      <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
                      {t('dashboard:status.reactivate.button')}
                    </Button>
                  </ReactivateDialog>
                  {evaluation.status === EvaluationStatus.Concluded && (
                    <CloseDialog evaluation={evaluation} onClose={onClose}>
                      <Button size="sm" variant="outline">
                        <XCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                        {t('dashboard:status.close.button')}
                      </Button>
                    </CloseDialog>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
